/**
 * V2.0 Phase 5 Cluster B closeout — post-auth `next` param resolver.
 *
 * Extracted into a leaf module (no react / react-router-dom deps) so it
 * can be unit-tested in isolation. Consumed by both `PublicOnlyRoute`
 * (declarative <Navigate />) and `Login.onSubmit` (imperative navigate).
 *
 * Accepts a raw (URL-encoded or not) `next` string and returns a safe
 * internal pathname to redirect to, OR `null` when the value should be
 * rejected. Guards against open-redirect vectors:
 *   - Empty / missing
 *   - Absolute URLs (`http://…`, `https://…`, `javascript:`, `data:`, `vbscript:`)
 *   - Protocol-relative (`//evil.com/…`)
 *   - Backslash tricks (`/\evil.com`)
 *   - Non-string / non-decodable payloads
 *   - Absurdly long inputs (>512 chars)
 *   - Control characters (newline, null, etc.)
 */
export function safeNext(raw) {
  if (raw == null) return null;
  if (typeof raw !== "string") return null;
  if (raw.length === 0 || raw.length > 512) return null;
  let decoded;
  try {
    decoded = decodeURIComponent(raw);
  } catch (_e) {
    return null;
  }
  if (!decoded.startsWith("/")) return null;
  // Reject protocol-relative and backslash-based hostname escapes.
  if (decoded.startsWith("//") || decoded.startsWith("/\\")) return null;
  if (/^\/*(https?:|javascript:|data:|vbscript:)/i.test(decoded)) return null;
  // Reject anything that decodes to include control characters.
  if (/[\x00-\x1F\x7F]/.test(decoded)) return null;
  return decoded;
}

/**
 * Cluster B iter3 tweak 2 — strip a specific query key from a raw
 * search string. Preserves ALL other keys (order-agnostic).
 *
 *   input:  "?next=%2F%2Fevil.com&ref=abc"  key="next"
 *   output: "?ref=abc"
 *
 *   input:  "?next=%2F%2Fevil.com"          key="next"
 *   output: ""    (empty string — no leading `?`)
 *
 *   input:  "?ref=abc"                      key="next"
 *   output: "?ref=abc"    (unchanged — key not present)
 *
 *   input:  ""                              key="next"
 *   output: ""
 *
 * Used by PublicOnlyRoute (and any other post-auth redirect site) to
 * make sure a rejected `next` value doesn't leak into the address bar
 * of the fallback destination.
 */
export function stripParam(rawSearch, key) {
  if (!key) return rawSearch || "";
  const params = new URLSearchParams(rawSearch || "");
  if (!params.has(key)) return rawSearch || "";
  params.delete(key);
  const s = params.toString();
  return s ? `?${s}` : "";
}

/**
 * Cluster B iter4 fix — single-source resolver used by BOTH
 * `PublicOnlyRoute` (declarative <Navigate />) and `Login.onSubmit`
 * (imperative useNavigate) so post-auth redirects preserve legit
 * query params (referral codes, utm_*, etc.) on every path — safe
 * next, rejected next, missing next, and next with its own query.
 *
 * Contract:
 *   - Returns `{ pathname, search }` — both fields always set.
 *   - `pathname` is either:
 *       • a validated internal path decoded from `rawNext` (safeNext
 *         accepted it), OR
 *       • `null` — caller must fall back to a default (/dashboard, etc.).
 *   - `search` is the merged query string (with leading `?` if non-empty,
 *     else empty string). Merge rules:
 *       • Starts with `next`'s OWN query (e.g. next=/foo?a=1 contributes a=1).
 *       • Preserved outer params (from rawSearch minus `next`) are then
 *         applied via `.set()` — they OVERRIDE next-embedded on key
 *         collision. Rationale: marketing/referral tracking is where the
 *         outer URL lives; deep-link deferred navigation shouldn't
 *         clobber a signup referral.
 *
 * Usage patterns:
 *   const { pathname, search } = resolveRedirect(nextParam, location.search);
 *   const dest = pathname || fallback;
 *   navigate({ pathname: dest, search, hash: location.hash }, { replace: true });
 */
export function resolveRedirect(rawNext, rawSearch) {
  const validNext = safeNext(rawNext);
  const preservedSearch = stripParam(rawSearch, "next");
  if (!validNext) {
    return { pathname: null, search: preservedSearch };
  }
  // safeNext already guarantees validNext starts with '/' and is not
  // protocol-relative. Parse against a fake base to cleanly extract
  // pathname + own query.
  let parsed;
  try {
    parsed = new URL(validNext, "http://x");
  } catch (_e) {
    // Defensive — should never trip given safeNext's guards.
    return { pathname: null, search: preservedSearch };
  }
  // Merge: start with next's own params, then apply preserved on top so
  // outer-URL params (ref, utm_*) win on collision.
  const merged = new URLSearchParams(parsed.search);
  new URLSearchParams(preservedSearch).forEach((value, key) => {
    merged.set(key, value);
  });
  const finalQuery = merged.toString();
  return {
    pathname: parsed.pathname,
    search: finalQuery ? `?${finalQuery}` : "",
  };
}
